/**
 * Zwischenspeicher für das fertig zusammengefügte Arbeitsdokument.
 *
 * Das Zusammenfügen ist der teure Teil: dreißig Dateien laden, jede Seite
 * kopieren, alles wieder ausschreiben – das dauerte beim Öffnen spürbar. Am
 * Ergebnis ändert sich aber nur etwas, wenn sich die Materialien ändern.
 * Genau das steckt im Schlüssel: Reihenfolge, ID und Änderungsdatum jeder
 * Datei. Kommt eine dazu, fällt eine weg, wird umsortiert oder durch
 * gespeicherte Anmerkungen neu geschrieben, ist der Schlüssel ein anderer
 * und es wird frisch gebaut.
 *
 * Die Materialien bleiben damit die einzige Wahrheit – der Zwischenspeicher
 * kann nie einen veralteten Stand zeigen, er kann höchstens nichts liefern.
 */

const DB_NAME = "lernapp-doc";
const STORE = "merged";
/**
 * So viele Stände bleiben liegen (verschiedene Themen). Höher als man beim
 * Bauen der Funktion denkt: an einem Schultag wechselt man leicht zwischen
 * mehr als vier Fächern, und bei schlechtem Internet (Schul-WLAN) kostet ein
 * erzwungener Neuaufbau – alle Dateien erneut laden – am meisten Zeit.
 * IndexedDB verkraftet das locker (Blobs, kein localStorage-Limit).
 */
const KEEP = 20;

export interface CachedMerge<Meta> {
  blob: Blob;
  meta: Meta;
}

interface Row<Meta> extends CachedMerge<Meta> {
  key: string;
  touched: number;
}

function openDb(name = DB_NAME, storeName = STORE): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(name, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(storeName)) db.createObjectStore(storeName, { keyPath: "key" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function run<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
  storeName = STORE
): Promise<T | null> {
  return new Promise((resolve) => {
    try {
      const request = action(db.transaction(storeName, mode).objectStore(storeName));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Schlüssel aus Reihenfolge, ID und Stand der beteiligten Dateien. */
export function mergeKey(topicId: string, parts: readonly { id: string; updated: string }[]): string {
  return `${topicId}|${parts.map((p) => `${p.id}:${p.updated}`).join(",")}`;
}

export async function readMerge<Meta>(key: string): Promise<CachedMerge<Meta> | null> {
  const db = await openDb();
  if (!db) return null;
  const row = await run<Row<Meta>>(db, "readonly", (store) => store.get(key));
  db.close();
  return row?.blob ? { blob: row.blob, meta: row.meta } : null;
}

export async function writeMerge<Meta>(key: string, value: CachedMerge<Meta>): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await run(db, "readwrite", (store) =>
    store.put({ key, touched: Date.now(), blob: value.blob, meta: value.meta } as Row<Meta>)
  );
  // Alte Stände wegräumen, damit der Speicher nicht unbegrenzt wächst.
  const rows = (await run<Row<unknown>[]>(db, "readonly", (store) => store.getAll())) ?? [];
  for (const row of rows.sort((a, b) => b.touched - a.touched).slice(KEEP)) {
    await run(db, "readwrite", (store) => store.delete(row.key));
  }
  db.close();
}

// Alter Datei-Zwischenspeicher – wird nur noch gelesen, damit schon geladene
// Dateien beim Umzug in die Geräteablage (localFiles.ts) nicht noch einmal
// übers Netz müssen.
// ponytail: die alte Datenbank wird nie geleert; löschen, sobald alle Geräte umgezogen sind.
const FILE_DB = "lernapp-files";
const FILE_STORE = "files";

export async function readFile(id: string, updated: string): Promise<Blob | null> {
  const db = await openDb(FILE_DB, FILE_STORE);
  if (!db) return null;
  const row = await run<{ updated: string; blob: Blob }>(db, "readonly", (s) => s.get(id), FILE_STORE);
  db.close();
  return row && row.updated === updated ? row.blob : null;
}
